import { useCallback, useMemo, useState } from 'react'
import { Title } from '@shared/ui/Typography/Title'
import s from './RegistrationForm.module.sass'
import { Button } from '@shared/ui/Actions/Button'
import { Checkbox } from '@shared/ui/Form/Single/Checkbox'
import { IconButton } from '@shared/ui/Actions/IconButton'
import IconArrow from '@icons/icon-arrow-quick-filter.svg'
import { Text } from '@shared/ui/Typography/Text'
import IconCheckmark from '@icons/icon-checkbox-checkmark.svg'
import IconPlus from '@icons/icon-counter-plus.svg'
import { PriceGroup } from '@pages/OwnerLanding/RegistrationForm/Forms/PriceGroup/PriceGroup'
import IconDots from '@icons/icon-three-dots.svg'
import { useClientResponsive } from '@shared/lib/hooks/useClientResponsive'
import { PromoModal } from '@pages/OwnerLanding/RegistrationForm/Forms/MarketingModals/PromoModal'
import { MainFeatureModal } from '@pages/OwnerLanding/RegistrationForm/Forms/MarketingModals/MainFeatureModal'
import { MaterialsModal } from '@pages/OwnerLanding/RegistrationForm/Forms/MarketingModals/MaterialsModal'
import { StaticsModal } from '@pages/OwnerLanding/RegistrationForm/Forms/MarketingModals/StaticsModal'
import Link from '@shared/ui/Navigation/Link'
import ConditionalWrap from '@shared/ui/Layout/Containers/ConditionalWrap'
import { useSearchParams } from 'next/navigation'

import {
  OptionIds,
  LK_OPTIONS,
  LK_PRO_OPTIONS,
  PROMO_OPTIONS,
  STATICS,
  MAIN_FEATURE,
  MATERIALS,
  type PromotionState,
  calculateDiscountedPrice,
} from '@shared/types/promotion'
import OwnerSurveyPicnic from '../../ui/OwnerSurveyPicnic/OwnerSurveyPicnic'

interface Props {
  onPrevClick: () => void
  onSubmit?: (checks: Partial<Record<OptionIds, boolean>>) => void
}

export const PromotionForm = ({ onSubmit, onPrevClick }: Props) => {
  const { isMobile } = useClientResponsive()

  const [promotionState, setPromotionState] = useState<PromotionState>({})

  const [isStepSkipable, setStateSkipable] = useState(false)

  // конфигурации на случай промки, можно сохранить, зависимости на флаге
  // Если изменять, то нужно изменить и в RegistrationForm.tsx для бека
  const searchParams = useSearchParams()
  const hasPromoActive = searchParams.get('special') === 'gastreet2026'
  const promoLkProOptions =
    searchParams.get('special') === 'gastreet2026'
      ? LK_PRO_OPTIONS.map((option) => ({ ...option, discount: option.discount ? 30 : undefined }))
      : LK_PRO_OPTIONS

  const handleStateFieldChange = useCallback(
    (field: keyof PromotionState) => (option: PromotionState[typeof field]) =>
      setPromotionState((prev) => ({ ...prev, [field]: option })),
    [],
  )

  const toggleSkip = () => {
    !isStepSkipable && setPromotionState({})
    setStateSkipable((old) => !old)
  }

  const [totalSum, totalDescription] = useMemo(
    () =>
      Object.values(promotionState)
        .flat()
        .reduce(
          (acc, currentValue) => {
            const discountedPrice = calculateDiscountedPrice(currentValue.price, currentValue.discount) || 0

            return [
              acc[0] + discountedPrice,
              acc[1] === '' ? currentValue?.longLabel : [acc[1], currentValue?.longLabel].join(', '),
            ]
          },
          [0, ''],
        ),
    [promotionState],
  )

  const [promoModal, setPromoModal] = useState<boolean>(false)
  const [materialsModal, setMaterialsModal] = useState<boolean>(false)
  const [mainFeatureModal, setMainFeatureModal] = useState<boolean>(false)
  const [staticsModal, setStaticsModal] = useState<boolean>(false)

  const handleSubmit = (e: React.SubmitEvent<HTMLFormElement>) => {
    e.stopPropagation()
    e.preventDefault()
    onSubmit &&
      onSubmit(
        isStepSkipable
          ? {
              [OptionIds.without_promotion]: true,
            }
          : Object.values(promotionState)
              .flat()
              .reduce((acc, val) => {
                return { ...acc, [val?.id]: true }
              }, {}),
      )
  }

  return (
    <form className={s.form} onSubmit={handleSubmit}>
      <PromoModal active={promoModal} onClose={() => setPromoModal(false)} />
      <MainFeatureModal active={mainFeatureModal} onClose={() => setMainFeatureModal(false)} />
      <MaterialsModal active={materialsModal} onClose={() => setMaterialsModal(false)} />
      <StaticsModal active={staticsModal} onClose={() => setStaticsModal(false)} />

      <Title tag='h1' sizes='h1 h2' className={s.title}>
        Продвижение ресторана
        <Text sizes='ML M' className={s['text-align-center']}>
          Выберите варианты продвижения для вашего ресторана
        </Text>
      </Title>

      <div className={`${s['promo-block']} ${s['gap']}`}>
        <div className={s['promo-block-column']}>
          <Title tag='h3' sizes='h3' className={s['local-title']}>
            Личный кабинет
            <Text sizes='M S' className={s.subtext}>
              Базовый набор функций для презентации ресторана на сайте и в приложении Афиши Рестораны
            </Text>
          </Title>

          <div className={s['card-big']}>
            <Text sizes='ML M' className={s['promo-card-row']}>
              <IconCheckmark />
              Публикация новостей и обновлений
            </Text>

            <Text sizes='ML M' className={s['promo-card-row']}>
              <IconCheckmark />
              Работа с отзывами
            </Text>

            <Text sizes='ML M' className={s['promo-card-row']}>
              <IconCheckmark />
              Статистика по просмотрам
            </Text>

            <Button
              sizes='XL M'
              mode='white-border'
              className={s['more-button']}
              onClick={() =>
                window.open('https://www.afisha.ru/rests/documents/restorator/2025_05_27/more_basic.pdf', '_blank')
              }
              type='button'>
              Подробнее
            </Button>
          </div>

          <div className={s['card-flex']}>
            <PriceGroup
              checked={promotionState.lkOptions}
              disabled={isStepSkipable}
              allSelectableOptions={LK_OPTIONS}
              onChange={handleStateFieldChange('lkOptions')}
            />
          </div>
        </div>

        <div className={s['promo-block-column']}>
          <Title tag='h3' sizes='h3' className={s['local-title']}>
            <span>
              Личный кабинет<span className={s.pro}>PRO</span>
            </span>
            <Text sizes='M S' className={s.subtext}>
              Дополнительные источники трафика и привлечения потенциальных гостей помимо базового функционала
            </Text>
          </Title>

          <div className={s['card-big']}>
            <Text sizes='ML M' className={s['promo-card-row']}>
              <IconCheckmark />
              Публикация новостей и обновлений
            </Text>

            <Text sizes='ML M' className={s['promo-card-row']}>
              <IconCheckmark />
              Работа с отзывами
            </Text>

            <Text sizes='ML M' className={s['promo-card-row']}>
              <IconCheckmark />
              Статистика по просмотрам
            </Text>

            <Text sizes='ML M' className={s['promo-card-row']}>
              <IconPlus className={s.red} />
              Сервис бронирования столов
            </Text>

            <Text sizes='ML M' className={s['promo-card-row']}>
              <IconPlus className={s.red} />
              Маркетинговая поддержка
            </Text>

            <Text sizes='ML M' className={s['promo-card-row']}>
              <IconPlus className={s.red} />
              Размещение на платформах партнеров
            </Text>

            <Button
              sizes='XL M'
              mode='white-border'
              className={s['more-button']}
              onClick={() =>
                window.open('https://www.afisha.ru/rests/documents/restorator/2025_05_27/more_pro.pdf', '_blank')
              }
              type='button'>
              Подробнее
            </Button>
          </div>

          <div className={s['card-flex']}>
            <PriceGroup
              checked={promotionState.lkOptions}
              allSelectableOptions={hasPromoActive ? promoLkProOptions : LK_PRO_OPTIONS}
              onChange={handleStateFieldChange('lkOptions')}
              disabled={isStepSkipable}
              showOriginalPrice={hasPromoActive}
            />
          </div>
        </div>
      </div>

      <div className={s['promo-group']}>
        <Title tag='h3' sizes='h3' className={s['local-title']}>
          Возможности Афиши Рестораны
          <Text sizes='M S' className={s.subtext}>
            Дополнительные инструменты {isMobile ? false : 'для продвижения'}
          </Text>
        </Title>

        <div className={s['promo-block']}>
          <div
            className={`${s['card-flex']} ${s['promo-block-column']}`}
            style={{ backgroundColor: 'hsla(213, 100%, 94%, 1)' }}>
            <Text sizes='L ML' className={s['card-title']}>
              Промо
              <IconButton
                sizes='M S'
                mode='white-border'
                icon={IconDots}
                className={s['weird-icon-button']}
                onClick={() => setPromoModal(true)}
                type='button'
              />
            </Text>

            <PriceGroup
              checked={promotionState.promoOptions}
              allSelectableOptions={PROMO_OPTIONS}
              onChange={handleStateFieldChange('promoOptions')}
              disabled={isStepSkipable}
              sizes='M'
            />
          </div>

          <div className={`${s['promo-block-column']} ${s['gap-6']}`}>
            <div className={s['card-flex']}>
              <Text sizes='L ML' className={s['card-title']}>
                Статика в подборке
                <IconButton
                  sizes='M S'
                  mode='white-border'
                  icon={IconDots}
                  className={s['weird-icon-button']}
                  onClick={() => setStaticsModal(true)}
                  type='button'
                />
              </Text>

              <PriceGroup
                checked={promotionState.statics}
                allSelectableOptions={STATICS}
                onChange={handleStateFieldChange('statics')}
                disabled={isStepSkipable}
                sizes='M'
              />
            </div>

            <div className={s['card-flex']}>
              <Text sizes='L ML' className={s['card-title']}>
                Фичер на главной
                <IconButton
                  sizes='M S'
                  mode='white-border'
                  icon={IconDots}
                  className={s['weird-icon-button']}
                  onClick={() => setMainFeatureModal(true)}
                  type='button'
                />
              </Text>

              <PriceGroup
                checked={promotionState.mainFeature}
                allSelectableOptions={MAIN_FEATURE}
                onChange={handleStateFieldChange('mainFeature')}
                disabled={isStepSkipable}
                sizes='M'
              />
            </div>
          </div>

          <div className={`${s['card-flex']} ${s['promo-block-column']}`}>
            <Text sizes='L ML' className={s['card-title']}>
              Редакционные материалы
              <IconButton
                sizes='M S'
                mode='white-border'
                icon={IconDots}
                className={s['weird-icon-button']}
                onClick={() => setMaterialsModal(true)}
                type='button'
              />
            </Text>

            <PriceGroup
              checked={promotionState.materials}
              allSelectableOptions={MATERIALS}
              onChange={handleStateFieldChange('materials')}
              multiselect={true}
              disabled={isStepSkipable}
              sizes='M'
            />
          </div>
        </div>
      </div>

      <div className={s['total-group']}>
        <Title tag='h3' sizes='h3' className={s.total}>
          Итого <span>{totalSum.toLocaleString('ru-RU')} ₽</span>
        </Title>

        <ConditionalWrap
          condition={!isMobile}
          wrap={(children) => <div className={s['desktop-bottom-line']}>{children}</div>}>
          <Text sizes='M S' className={s['total-description']}>
            {totalDescription}
          </Text>

          <Text sizes='M S' className={s.subtext}>
            *Все цены указаны без НДС
          </Text>
        </ConditionalWrap>
      </div>

      <OwnerSurveyPicnic className={s.survey} />

      <div className={s['submit-block']}>
        <div className={s['checkbox-line']} onClick={toggleSkip}>
          <Checkbox
            mode={'checkbox'}
            option={{
              id: 'skip',
              name: 'skip',
            }}
            checked={isStepSkipable}
            onChange={toggleSkip}
          />
          <Text sizes='ML M'>Добавить заведение без продвижения</Text>
        </div>

        <div className={s['buttons-row']}>
          <IconButton
            className={s['prev-button']}
            icon={IconArrow}
            sizes='XL'
            mode='white-border'
            onClick={onPrevClick}
            type='button'
          />

          <Button
            type='submit'
            className={s['next-button']}
            sizes='XL'
            mode='black'
            disabled={totalSum === 0 && !isStepSkipable}>
            Оставить заявку
          </Button>
        </div>

        <span className={s['bottom-subtext']}>
          Нажимая кнопку «Оставить заявку», вы принимаете{' '}
          <Link
            href='https://www.afisha.ru/rests/documents/restorator/2025_05_27/offerta.docx'
            target={'_blank'}
            className={s.link}
            onClick={(e) => e.stopPropagation()}>
            условия оферты
          </Link>
        </span>
      </div>
    </form>
  )
}
